"use client";
import {
	GetObjectCommand,
	HeadObjectCommand,
	PutObjectCommand,
	S3Client,
} from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fetchAuthSession } from "aws-amplify/auth";
import { useCallback } from "react";
import {
	extractS3KeyFromUri,
	getTermHtmlKey,
	getTermsJsonKey,
	RESOURCE_S3_BUCKET,
} from "@/config/terms";
import type { TermType, TermVersion } from "../types/terms";

interface S3Operations {
	// 利用規約履歴取得（AWS SDK経由でS3から）
	getTermVersionHistory: (type: TermType) => Promise<TermVersion[]>;
	// 利用規約HTML取得（AWS SDK経由でS3から、S3 URIを指定）
	getTermHtml: (s3Uri: string) => Promise<string>;
	// 利用規約HTMLをS3にアップロード
	uploadTermHtml: (
		type: TermType,
		version: string,
		htmlContent: string,
	) => Promise<{ s3Uri: string }>;
}

/**
 * S3オブジェクトの存在チェック
 * @returns true: 存在する, false: 存在しない
 */
const checkObjectExists = async (
	client: S3Client,
	bucket: string,
	key: string,
): Promise<boolean> => {
	try {
		await client.send(new HeadObjectCommand({ Bucket: bucket, Key: key }));
		return true;
	} catch (error) {
		if (error instanceof Error && error.name === "NotFound") {
			return false;
		}
		throw error;
	}
};

export const useS3Operations = (): S3Operations => {
	const s3Client = useCallback(async () => {
		const session = await fetchAuthSession();
		const idToken = session.tokens?.idToken?.toString();

		if (!idToken) {
			throw new Error("ID token is required but not found.");
		}
		const loginData = {
			[`cognito-idp.${import.meta.env.VITE_AWS_REGION}.amazonaws.com/${import.meta.env.VITE_COGNITO_USERPOOL_ID}`]:
				idToken,
		};

		return new S3Client({
			region: import.meta.env.VITE_AWS_REGION,
			credentials: fromCognitoIdentityPool({
				clientConfig: { region: import.meta.env.VITE_AWS_REGION },
				identityPoolId: import.meta.env.VITE_COGNITO_IDPOOL_ID || "",
				logins: loginData,
			}),
		});
	}, []);

	// 利用規約バージョン履歴を取得
	const getTermVersionHistory = useCallback(
		async (type: TermType): Promise<TermVersion[]> => {
			try {
				const client = await s3Client();
				const key = getTermsJsonKey(type);
				const getCommand = new GetObjectCommand({
					Bucket: RESOURCE_S3_BUCKET,
					Key: key,
				});
				const url = await getSignedUrl(client, getCommand, { expiresIn: 60 });
				const response = await fetch(url);
				if (!response.ok) {
					throw new Error(`Failed to fetch terms.json: ${response.status} ${response.statusText}`);
				}
				const data: TermVersion[] = await response.json();
				return data;
			} catch (error) {
				throw new Error(
					`Failed to get term version history: ${error instanceof Error ? error.message : String(error)}`,
				);
			}
		},
		[s3Client],
	);

	// 利用規約HTMLを取得
	const getTermHtml = useCallback(
		async (s3Uri: string): Promise<string> => {
			if (!RESOURCE_S3_BUCKET) {
				throw new Error("NEXT_PUBLIC_S3_BUCKET environment variable is not set");
			}
			try {
				const client = await s3Client();
				const key = extractS3KeyFromUri(s3Uri);
				const getCommand = new GetObjectCommand({
					Bucket: RESOURCE_S3_BUCKET,
					Key: key,
				});
				const url = await getSignedUrl(client, getCommand, { expiresIn: 60 });
				const response = await fetch(url);
				if (!response.ok) {
					throw new Error(`Failed to fetch term.html: ${response.status} ${response.statusText}`);
				}
				return await response.text();
			} catch (error) {
				throw new Error(
					`Failed to get term HTML: ${error instanceof Error ? error.message : String(error)}`,
				);
			}
		},
		[s3Client],
	);

	// 利用規約HTMLをS3にアップロード
	const uploadTermHtml = useCallback(
		async (type: TermType, version: string, htmlContent: string): Promise<{ s3Uri: string }> => {
			if (!RESOURCE_S3_BUCKET) {
				throw new Error("NEXT_PUBLIC_S3_BUCKET environment variable is not set");
			}

			const client = await s3Client();
			const key = getTermHtmlKey(type, version);

			// 同じキーが存在するかチェック
			const exists = await checkObjectExists(client, RESOURCE_S3_BUCKET, key);
			if (exists) {
				throw new Error(`Version ${version} already exists`);
			}

			// アップロード実行
			const putCommand = new PutObjectCommand({
				Bucket: RESOURCE_S3_BUCKET,
				Key: key,
				Body: htmlContent,
				ContentType: "text/html; charset=utf-8",
			});
			await client.send(putCommand);

			return { s3Uri: `s3://${RESOURCE_S3_BUCKET}/${key}` };
		},
		[s3Client],
	);

	return {
		getTermVersionHistory,
		getTermHtml,
		uploadTermHtml,
	};
};
